import React, { useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import ListTable from './ListTable';
import { useUsers } from '../context/UserContext';
import ReassignModal from './ReassignModal';
import ResetPasswordModal from './ResetPasswordModal';
import { Ban, Pencil, XOctagon, Key, Check2Circle, ArrowLeftRight, PlusCircle } from 'react-bootstrap-icons';
import AnchorTag from './AnchorTag';
import ReassignProjectModal from './ReassignProjectModal';
import { formatDateToMMDDYYYY } from '../utils/dateUtil';

// ---------- existing helpers (kept) ----------
function getRegionAndAreaNames(input, regions) {
  if (!regions) return [];
  return input.map(({ regionId, areas }) => {
    const region = regions.find(r => r.id === regionId);
    if (!region) return { regionId, regionName: null, areaNames: [] };

    const areaNames = areas
      .map(areaId => {
        const area = region.areas.find(a => a.id === areaId);
        return area ? area.name : null;
      })
      .filter(name => name !== null);

    return { regionId, regionName: region.name, areaNames };
  });
}
function getUserColumns({ regions, actions = [] }) {
  const columnHelper = createColumnHelper();

  return [
    columnHelper.accessor('id', {
      header: 'ID',
      cell: info => info.getValue(),
    }),
    columnHelper.accessor('username', {
      header: 'Username',
      cell: info => info.getValue(),
    }),
    columnHelper.accessor('birthDate', {
      header: 'Birthdate',
      cell: info => formatDateToMMDDYYYY(info.getValue()),
    }),
    columnHelper.accessor('roles', {
      id: 'roleNames',
      header: 'Roles',
      cell: info => {
        const roles = info.getValue() || [];
        return roles.map(role => <div key={`role-${role.id}`}>{role.name}</div>);
      },
    }),
    columnHelper.accessor('roles', {
      id: 'roleAssignments',
      header: 'Assignment',
      cell: info => {
        const roles = info.getValue() || [];
        const regionAreaMap = new Map();
        roles.forEach(role => {
          if (Array.isArray(role?.regionIds)) {
            const resolved = getRegionAndAreaNames(role.regionIds, regions);
            resolved.forEach(({ regionName, areaNames }) => {
              if (!regionName) return;
              if (!regionAreaMap.has(regionName)) regionAreaMap.set(regionName, new Set());
              areaNames.forEach(name => regionAreaMap.get(regionName).add(name));
            });
          }
        });

        return Array.from(regionAreaMap.entries()).map(([regionName, areaSet]) => (
          <div key={regionName}>
            <strong>{regionName}</strong> – {Array.from(areaSet).join(', ')}
          </div>
        ));
      },
    }),
    actions.length > 0 &&
      columnHelper.display({
        id: 'action',
        header: 'Action',
        cell: info => {
          const row = info.row.original;
          return (
            <div className="d-flex gap-2">
              {actions.map(({ label, onClick }) => (
                <AnchorTag key={label} onClick={() => onClick(row)} hoverText={label}>
                  {label === 'Reassign' && <ArrowLeftRight />}
                  {label === 'Reset Password' && <Key />}
                  {label === 'Delete' && <XOctagon />}
                  {label === 'Edit' && <Pencil />}
                  {label === 'Approve' && <PlusCircle />}
                  {label === 'Activate' && <Check2Circle />}
                  {label === 'Deactivate' && <Ban />}
                </AnchorTag>
              ))}
            </div>
          );
        },
      }),
  ].filter(Boolean);
}

// ---------- NEW helpers for Project Users ----------
const projectNameOf = (role) =>
  role?.project?.name ?? role?.Project?.name ?? (role?.projectId ? `Project #${role.projectId}` : '—');

const summarizeAssignments = (role) => {
  const items = Array.isArray(role?.regionIds) ? role.regionIds : [];
  if (!items.length) return '—';
  return items
    .map(({ regionId, areas }) => {
      const arr = Array.isArray(areas) ? areas : [];
      return `R${regionId}${arr.length ? `: ${arr.join(', ')}` : ''}`;
    })
    .join(' • ');
};

// ---------- NEW: Project Users table (project-first reassignment) ----------
function ProjectUsersTable({
  users,
  projects = [],
  onReassigned,               // callback to refetch users after change
}) {
  const { hasPermission } = useUsers();
  const canDelete = hasPermission('Users', 'Delete');
  const canUpdate = hasPermission('Users', 'Update');

  const [showReassign, setShowReassign] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const openReassign = (user) => {
    setSelectedUser(user);
    setShowReassign(true);
  };

  const closeReassign = () => setShowReassign(false);

  const columnHelper = createColumnHelper();

  const columns = [
    // Full Name
    columnHelper.accessor(
      row => `${row.firstName || ''} ${row.lastName || ''}`.trim(),
      {
        id: 'fullName',
        header: 'Full Name',
        cell: info => {
          const u = info.row.original;
          const name = `${u.firstName || ''} ${u.lastName || ''}`.trim();
          return <div>{name || '—'}</div>;
        },
      }
    ),

    // Username
    columnHelper.accessor(row => row.username, {
      id: 'username',
      header: 'Username',
      cell: info => <div>{info.getValue() || '—'}</div>,
    }),

    // Project (list per-role)
    columnHelper.accessor('roles', {
      id: 'project',
      header: 'Project',
      cell: info => {
        const roles = info.getValue() || [];
        if (!roles.length) return <span className="text-muted">—</span>;
        return (
          <div className="d-flex flex-column gap-1">
            {roles.map(r => (
              <div key={`${info.row.original.id}-${r.id || r.name}`}>
                {projectNameOf(r)}
              </div>
            ))}
          </div>
        );
      },
    }),

    // Role (list per-role) + inline change icon
    columnHelper.accessor('roles', {
      id: 'role',
      header: 'Role',
      cell: info => {
        const u = info.row.original;
        const roles = info.getValue() || [];
        if (!roles.length) return <span className="text-muted">—</span>;
        return (
          <div className="d-flex flex-column gap-1">
            {roles.map(r => (
              <div
                key={`${u.id}-${r.id || r.name}`}
                className="d-flex align-items-center gap-2"
              >
                <span>{r.name}</span>
              </div>
            ))}
          </div>
        );
      },
    }),

    // Actions (row-level)
    (canUpdate || canDelete) &&
      columnHelper.display({
        id: 'action',
        header: 'Action',
        cell: info => {
          const row = info.row.original;
          return (
            <div className="d-flex gap-2">
              {canUpdate && (
                <AnchorTag hoverText="Reassign Project/Role" onClick={() => openReassign(row)}>
                  <ArrowLeftRight />
                </AnchorTag>
              )}
            </div>
          );
        },
      }),
  ].filter(Boolean);

  return (
    <>
      <ListTable tableId="project-users" data={users} columns={columns} />
      {selectedUser && (
        <ReassignProjectModal
          show={showReassign}
          onClose={closeReassign}
          onReassign={() => {
            onReassigned?.();
            closeReassign();
          }}
          user={selectedUser}
          projects={projects}
          // optional: initialProjectId={someId}
        />
      )}
    </>
  );
}


function ActiveUsersTable({ readOnly, users, regions, refetchUsers, roles, handleDeactivate, handleDelete }) {
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const { isAdmin, hasPermission } = useUsers();

  const openReassignModal = (user) => {
    setSelectedUser(user);
    setShowReassignModal(true);
  };

  const openResetPasswordModal = (user) => {
    setSelectedUser(user);
    setShowResetPasswordModal(true);
  };

  const canUpdate = hasPermission('Users', 'Update');
  const canDelete = hasPermission('Users', 'Delete');
  const columns = getUserColumns({
    regions,
   ...(!readOnly && (canUpdate || canDelete) ? {
        actions: [
          ...(canUpdate ? [{ label: 'Reassign', onClick: openReassignModal }] : []),
          ...(canUpdate ? [{ label: 'Reset Password', onClick: openResetPasswordModal }] : []),
          ...(canDelete ? [{ label: 'Deactivate', onClick: handleDeactivate }] : []),
          ...(canDelete ? [{ label: 'Delete', onClick: handleDelete, variant: 'outline-danger' }] : []),
        ]
      }
    : {})
  });

  return (
    <>
      <ListTable tableId='active-users' data={users} columns={columns} />
      {selectedUser && (
        <>
          <ReassignModal
            user={selectedUser}
            roles={roles}
            show={showReassignModal}
            onClose={() => setShowReassignModal(false)}
            onReassign={() => {
              refetchUsers();
              setShowReassignModal(false);
            }}
          />
          <ResetPasswordModal
            user={selectedUser}
            show={showResetPasswordModal}
            onClose={() => setShowResetPasswordModal(false)}
            refetch={refetchUsers}
          />
        </>
      )}
    </>
  );
}

function InactiveUsersTable({ readOnly, users, handleApprove, handleDelete, regions, refetchUsers, roles }) {
  const { isAdmin, hasPermission } = useUsers();
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);

  const openReassignModal = (user) => {
    setSelectedUser(user);
    setShowReassignModal(true);
  };

  const openResetPasswordModal = (user) => {
    setSelectedUser(user);
    setShowResetPasswordModal(true);
  };

  const canUpdate = hasPermission('Users', 'Update');
  const canDelete = hasPermission('Users', 'Delete');

  const columns = getUserColumns({
    regions,
   ...(!readOnly && (canUpdate || canDelete)
      ? {
          actions: [
            ...(canUpdate ? [{ label: 'Approve', onClick: handleApprove }] : []),
            ...(canUpdate ? [{ label: 'Reassign', onClick: openReassignModal }] : []),
            ...(isAdmin ? [{ label: 'Reset Password', onClick: openResetPasswordModal }] : []),
            ...(canDelete ? [{ label: 'Delete', onClick: handleDelete, variant: 'outline-danger' }] : []),
          ]
        }
      : {})
  });

  return (
    <>
      <ListTable tableId='inactive-users' data={users} columns={columns} />
      {selectedUser && (
        <>
          <ReassignModal
            user={selectedUser}
            roles={roles}
            show={showReassignModal}
            onClose={() => setShowReassignModal(false)}
            onReassign={() => {
              refetchUsers();
              setShowReassignModal(false);
            }}
          />
          <ResetPasswordModal
            user={selectedUser}
            show={showResetPasswordModal}
            onClose={() => setShowResetPasswordModal(false)}
            refetch={refetchUsers}
          />
        </>
      )}
    </>
  );
}

function DisabledUsersTable({ readOnly, users, regions, handleActivate, handleDelete }) {
  const [selectedUser, setSelectedUser] = useState(null);
  const { isAdmin, hasPermission } = useUsers();

  const canDelete = hasPermission('Users', 'Delete');
  // Only allow activate for disabled users
  const columns = getUserColumns({
    regions,
    ...(!readOnly && (isAdmin || canDelete)
      ? {
          actions: [
          ...(isAdmin ? [{ label: 'Activate', onClick: handleActivate }] : []),
          ...(canDelete ? [{ label: 'Delete', onClick: handleDelete, variant: 'outline-danger' }] : []),
        ]
        }
      : {})
  });

  return <ListTable tableId="disabled-users" data={users} columns={columns} />;
}

export { ActiveUsersTable, InactiveUsersTable, DisabledUsersTable, ProjectUsersTable };
